import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { UserEmailBadge } from '$src/components/company-portal/UserEmailBadge'
import { customerHooks, visitHooks } from '$src/lib/trpcHooks'
import {
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  NativeSelect,
  Stack,
  Text,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { AppPage } from '@markku/components'
import type { VisitWithRelations } from '@markku/types'
import { formatDate } from '@markku/utils'
import { IconCalendarEvent } from '@tabler/icons-react'
import { keepPreviousData } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { z } from 'zod'

const PAGE_SIZE = 25

const searchSchema = z.object({
  property: z.string().optional(),
  status: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
})

const statusColor: Record<string, string> = {
  completed: 'green',
  scheduled: 'blue',
  in_progress: 'yellow',
  cancelled: 'gray',
}

const statusLabel: Record<string, ReturnType<typeof msg>> = {
  completed: msg`Completed`,
  scheduled: msg`Scheduled`,
  in_progress: msg`In progress`,
  cancelled: msg`Cancelled`,
}

function buildVisitFilters({
  propertyIds,
  propertyFilter,
  statusFilter,
  startDate,
  endDate,
}: {
  propertyIds: number[]
  propertyFilter: string
  statusFilter: string
  startDate: Date | null
  endDate: Date | null
}) {
  const filters: Record<string, unknown> = {}

  filters.propertyId =
    propertyFilter !== 'all'
      ? { eq: Number(propertyFilter) }
      : { in: propertyIds }

  if (statusFilter !== 'all') {
    filters.status = { eq: statusFilter }
  }

  if (startDate || endDate) {
    const timeFilter: Record<string, string> = {}
    if (startDate) timeFilter.gte = startDate.toISOString()
    if (endDate) {
      const end = new Date(endDate)
      end.setHours(23, 59, 59, 999)
      timeFilter.lte = end.toISOString()
    }
    filters.startTime = timeFilter
  }

  return filters
}

function CompanyVisitList() {
  const { t } = useLingui()
  const { companyId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const [loadLimit, setLoadLimit] = useState(PAGE_SIZE)

  const propertyFilter = search.property || 'all'
  const statusFilter = search.status || 'all'
  const startDate = search.startDate ? new Date(search.startDate) : null
  const endDate = search.endDate ? new Date(search.endDate) : null

  const updateSearch = (updates: Partial<z.infer<typeof searchSchema>>) => {
    setLoadLimit(PAGE_SIZE)
    navigate({
      to: '/company-portal/$companyId/visits',
      params: { companyId },
      search: { ...search, ...updates },
      replace: true,
    })
  }

  const { data: company, isLoading: isLoadingCompany } =
    customerHooks.useGetById(Number(companyId))

  const properties = company?.properties || []
  const propertyIds = properties.map((p) => p.id)

  const filters = buildVisitFilters({
    propertyIds,
    propertyFilter,
    statusFilter,
    startDate,
    endDate,
  })

  const { data: visitsData, isLoading: isLoadingVisits } = visitHooks.useList(
    {
      filters,
      orderBy: 'startTime',
      orderDirection: 'desc',
      pageSize: loadLimit,
    },
    {
      enabled: propertyIds.length > 0,
      placeholderData: keepPreviousData,
    } as any,
  )

  const isInitialLoading =
    isLoadingCompany ||
    (propertyIds.length > 0 && isLoadingVisits && !visitsData)
  const isLoadingMore = isLoadingVisits && !!visitsData

  const visits = (visitsData?.data || []) as VisitWithRelations[]
  const totalVisits = visitsData?.pagination?.total || 0
  const hasMore = loadLimit < totalVisits

  const propertyOptions = [
    { value: 'all', label: t`All properties` },
    ...properties.map((p) => ({
      value: String(p.id),
      label: p.address?.street || t`Property ${p.id}`,
    })),
  ]

  const statusOptions = [
    { value: 'all', label: t`All statuses` },
    { value: 'completed', label: t`Completed` },
    { value: 'scheduled', label: t`Scheduled` },
    { value: 'cancelled', label: t`Cancelled` },
  ]

  return (
    <AppPage
      title={company?.name || t`Visits`}
      backTarget="/company-portal"
      rightSection={<UserEmailBadge />}
    >
      <Stack gap="lg">
        <Card withBorder radius="md" p="md">
          <Stack gap="sm">
            <Group grow>
              <NativeSelect
                value={propertyFilter}
                onChange={(e) =>
                  updateSearch({
                    property:
                      e.target.value === 'all' ? undefined : e.target.value,
                  })
                }
                data={propertyOptions}
              />
              <NativeSelect
                value={statusFilter}
                onChange={(e) =>
                  updateSearch({
                    status:
                      e.target.value === 'all' ? undefined : e.target.value,
                  })
                }
                data={statusOptions}
              />
            </Group>

            <Group grow>
              <DatePickerInput
                placeholder={t`Start date`}
                value={startDate}
                onChange={(v) =>
                  updateSearch({
                    startDate: v
                      ? new Date(String(v)).toISOString()
                      : undefined,
                  })
                }
                maxDate={endDate || undefined}
                clearable
              />
              <DatePickerInput
                placeholder={t`End date`}
                value={endDate}
                onChange={(v) =>
                  updateSearch({
                    endDate: v ? new Date(String(v)).toISOString() : undefined,
                  })
                }
                minDate={startDate || undefined}
                clearable
              />
            </Group>

            {!isInitialLoading && (
              <Text size="sm" c="dimmed">
                {hasMore ? (
                  <Trans>
                    {visits.length} of {totalVisits} visits
                  </Trans>
                ) : (
                  <Trans>{totalVisits} visits</Trans>
                )}
              </Text>
            )}
          </Stack>
        </Card>

        {isInitialLoading && (
          <Center py="xl">
            <Loader size="md" />
          </Center>
        )}

        {!isInitialLoading && visits.length === 0 && (
          <Card withBorder radius="md" p="xl" bg="gray.0">
            <Center>
              <Stack align="center" gap="xs">
                <IconCalendarEvent
                  size={48}
                  stroke={1.5}
                  color="var(--mantine-color-gray-5)"
                />
                <Text c="dimmed" fw={500}>
                  <Trans>No visits found</Trans>
                </Text>
              </Stack>
            </Center>
          </Card>
        )}

        {!isInitialLoading && visits.length > 0 && (
          <Stack gap="md">
            {visits.map((visit) => {
              const address = visit.property?.address
              return (
                <Card
                  key={visit.id}
                  withBorder
                  radius="md"
                  p="md"
                  style={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate({
                      to: '/company-portal/$companyId/visits/$visitId',
                      params: {
                        companyId,
                        visitId: String(visit.id),
                      },
                    })
                  }
                >
                  <Group justify="space-between" align="flex-start">
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Text fw={600}>
                        {formatDate(new Date(visit.startTime))}
                      </Text>
                      {address && (
                        <Text size="sm" c="dimmed">
                          {address.street}
                          {address.city ? `, ${address.city}` : ''}
                        </Text>
                      )}
                      {visit.visitTasks?.length ? (
                        <Text size="sm" c="dimmed" lineClamp={1}>
                          {visit.visitTasks
                            .map((vt) => vt.maintenanceTask?.name)
                            .filter(Boolean)
                            .join(', ')}
                        </Text>
                      ) : null}
                    </Stack>
                    <Badge
                      size="sm"
                      color={statusColor[visit.status] || 'gray'}
                      variant="light"
                    >
                      {statusLabel[visit.status]
                        ? t(statusLabel[visit.status])
                        : visit.status}
                    </Badge>
                  </Group>
                </Card>
              )
            })}

            {hasMore && (
              <Button
                variant="light"
                fullWidth
                loading={isLoadingMore}
                onClick={() => setLoadLimit((l) => l + PAGE_SIZE)}
              >
                <Trans>Load more visits</Trans>
              </Button>
            )}
          </Stack>
        )}
      </Stack>
    </AppPage>
  )
}

export const Route = createFileRoute('/_app/company-portal/$companyId/visits/')(
  {
    component: CompanyVisitList,
    validateSearch: searchSchema,
  },
)
